import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import FilterBar from "@/components/FilterBar";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import ColumnFilterRow, { applyColumnFilters, columnFiltersActive } from "@/components/ColumnFilterRow";
import { useCompanyFilter } from "@/lib/companyFilter";
import { Download, FileSpreadsheet } from "lucide-react";

/**
 * Generic Reports list with filter-first UI.
 * Routes:
 *   /reports/employees — directory filter (status, dept, type)
 *   /reports/attendance — month/year, status counts per employee
 *   /reports/payroll — month/year, salary register summary
 */

const downloadFile = async (url, filename) => {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const ct = (res.headers["content-type"] || "").toLowerCase();
    if (ct.includes("application/json")) {
      const text = await res.data.text();
      let detail = "Download failed";
      try {
        detail = JSON.parse(text)?.detail || detail;
      } catch (_) {
        /* ignore */
      }
      alert(typeof detail === "string" ? detail : "Download failed");
      return;
    }
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (ex) {
    const d = ex?.response?.data;
    if (d instanceof Blob) {
      try {
        const j = JSON.parse(await d.text());
        alert(typeof j?.detail === "string" ? j.detail : "Download failed");
        return;
      } catch (_) {
        /* fall through */
      }
    }
    alert(typeof d?.detail === "string" ? d.detail : "Download failed");
  }
};

/* ─────────── /reports/employees ─────────── */
// Phase 18 — Column catalog (single source of truth). Each entry:
//   { key, label, type: text|number|date|enum, get: (e, ctx) => value, options? (for enum) }
const ALL_COLS = [
  { key: "code",            label: "Code",         type: "text",   get: (e) => e.basic?.code },
  { key: "first_name",      label: "First Name",   type: "text",   get: (e) => e.basic?.first_name },
  { key: "middle_name",     label: "Middle Name",  type: "text",   get: (e) => e.basic?.middle_name || "" },
  { key: "last_name",       label: "Last Name",    type: "text",   get: (e) => e.basic?.last_name },
  { key: "full_name",       label: "Full Name",    type: "text",   get: (e) => [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ") },
  { key: "email",           label: "Email",        type: "text",   get: (e) => e.contact?.email || "" },
  { key: "phone",           label: "Phone",        type: "text",   get: (e) => e.contact?.phone || "" },
  { key: "gender",          label: "Gender",       type: "enum",   get: (e) => e.basic?.gender || "", options: ["Male","Female","Other"] },
  { key: "dob",             label: "Date of Birth",type: "date",   get: (e) => e.basic?.date_of_birth || "" },
  { key: "marital_status",  label: "Marital",      type: "text",   get: (e) => e.basic?.marital_status || "" },
  { key: "blood_group",     label: "Blood Group",  type: "text",   get: (e) => e.basic?.blood_group || "" },
  { key: "city",            label: "City",         type: "text",   get: (e) => e.contact?.city || "" },
  { key: "state",           label: "State",        type: "text",   get: (e) => e.contact?.state || "" },
  { key: "department",      label: "Department",   type: "enum",   get: (e, ctx) => ctx.depMap[e.employment?.department_id] || "", options: [] /* filled at runtime */ },
  { key: "designation",     label: "Designation",  type: "enum",   get: (e, ctx) => ctx.desigMap[e.employment?.designation_id] || "", options: [] },
  { key: "type",            label: "Empl. Type",   type: "enum",   get: (e) => e.employment?.employment_type || "", options: ["Full-time","Part-time","Contract","Intern"] },
  { key: "status",          label: "Status",       type: "enum",   get: (e) => e.employment?.status || "", options: ["Active","Onboarding","Probation","Notice Period","Resigned","Inactive","Terminated", "Abscond",] },
  { key: "joining",         label: "Joining Date", type: "date",   get: (e) => e.employment?.joining_date || "" },
  { key: "probation_end",   label: "Probation End",type: "date",   get: (e) => e.employment?.probation_end_date || "" },
  { key: "internship_end",  label: "Intern End",   type: "date",   get: (e) => e.employment?.internship_end_date || "" },
  { key: "resignation",     label: "Resigned",     type: "date",   get: (e) => e.employment?.resignation_date || "" },
  { key: "lwd",             label: "LWD",          type: "date",   get: (e) => e.employment?.last_working_date || "" },
  { key: "manager",         label: "Manager",      type: "text",   get: (e, ctx) => ctx.empMap[e.employment?.manager_id] || "" },
  { key: "location",        label: "Location",     type: "text",   get: (e, ctx) => ctx.locMap[e.employment?.location_id] || e.employment?.work_location || "" },
  { key: "ctc",             label: "Annual CTC",   type: "number", get: (e) => Number(e.salary?.annual_ctc || 0) },
  { key: "pan",             label: "PAN",          type: "text",   get: (e) => e.salary?.pan || "" },
  { key: "uan",             label: "UAN",          type: "text",   get: (e) => e.salary?.uan || "" },
  { key: "bank",            label: "Bank",         type: "text",   get: (e) => e.salary?.bank_name || "" },
  { key: "account_number",  label: "Account No",   type: "text",   get: (e) => e.salary?.account_number || "" },
  { key: "ifsc",            label: "IFSC",         type: "text",   get: (e) => e.salary?.ifsc || "" },
];

/** Map report column keys → /exports/employees.* field keys */
const REPORT_TO_EXPORT_FIELD = {
  code: "code",
  first_name: "first_name",
  last_name: "last_name",
  full_name: "full_name",
  email: "email",
  phone: "phone",
  gender: "gender",
  dob: "dob",
  city: "city",
  state: "state",
  department: "department",
  designation: "designation",
  type: "employment_type",
  status: "status",
  joining: "joining_date",
  ctc: "ctc",
  pan: "pan",
  uan: "uan",
  bank: "bank_name",
  account_number: "account_number",
  ifsc: "ifsc",
  location: "work_location",
};

const DEFAULT_VISIBLE = ["code","full_name","department","type","status","joining","resignation","lwd"];
const DATE_OPS = [
  { value: "eq", label: "=" }, { value: "gt", label: ">" }, { value: "lt", label: "<" },
  { value: "gte", label: "≥" }, { value: "lte", label: "≤" }, { value: "between", label: "Between" },
];
const NUM_OPS = DATE_OPS;
const TEXT_OPS = [{ value: "contains", label: "Contains" }, { value: "eq", label: "Equals" }];

function applyFilter(val, op, a, b) {
  if (val === null || val === undefined || val === "") return op === "eq" && (a === "" || a === undefined);
  const v = String(val).toLowerCase();
  if (op === "contains") return v.includes(String(a || "").toLowerCase());
  if (op === "eq") return String(val) === String(a);
  // numeric / date
  const nv = isNaN(Number(val)) ? val : Number(val);
  const na = isNaN(Number(a)) ? a : Number(a);
  const nb = isNaN(Number(b)) ? b : Number(b);
  if (op === "gt") return nv > na;
  if (op === "lt") return nv < na;
  if (op === "gte") return nv >= na;
  if (op === "lte") return nv <= na;
  if (op === "between") return nv >= na && nv <= nb;
  return true;
}

export function EmployeeReports() {
  const { companyFilter, empParams, companyId } = useCompanyFilter();
  const [emps, setEmps] = useState([]);
  const [deps, setDeps] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [locs, setLocs] = useState([]);
  const [search, setSearch] = useState("");
  // visible columns
  const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE);
  const [showColPicker, setShowColPicker] = useState(false);
  // advanced filters: array of { key, op, a, b }
  const [advFilters, setAdvFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [colFilters, setColFilters] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/employees", { params: empParams }),
      api.get("/departments"),
      api.get("/designations"),
      api.get("/locations").catch(() => ({ data: [] })),
    ]).then(([e, d, des, l]) => {
      setEmps(e.data); setDeps(d.data); setDesigs(des.data); setLocs(l.data || []);
    });
  }, [companyId, empParams]);

  const depMap   = useMemo(() => Object.fromEntries(deps.map((d) => [d.id, d.name])), [deps]);
  const desigMap = useMemo(() => Object.fromEntries(desigs.map((d) => [d.id, d.title])), [desigs]);
  const locMap   = useMemo(() => Object.fromEntries(locs.map((l) => [l.id, l.name])), [locs]);
  const empMap   = useMemo(() => Object.fromEntries(emps.map((e) => [e.id, [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ")])), [emps]);
  const ctx = useMemo(() => ({ depMap, desigMap, locMap, empMap }), [depMap, desigMap, locMap, empMap]);

  // hydrate dynamic enum options for department/designation
  const colsHydrated = useMemo(() => ALL_COLS.map((c) => {
    if (c.key === "department") return { ...c, options: deps.map((d) => d.name) };
    if (c.key === "designation") return { ...c, options: desigs.map((d) => d.title) };
    return c;
  }), [deps, desigs]);
  const colByKey = useMemo(() => Object.fromEntries(colsHydrated.map((c) => [c.key, c])), [colsHydrated]);

  const colGetValue = useCallback((e, key) => {
    const col = colByKey[key];
    return col ? col.get(e, ctx) : "";
  }, [colByKey, ctx]);

  // Apply search + advanced filters + per-column filters
  const filtered = useMemo(() => {
    const base = emps.filter((e) => {
      if (search) {
        const s = search.toLowerCase();
        const full = [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ").toLowerCase();
        if (!full.includes(s) && !(e.basic?.code || "").toLowerCase().includes(s)) return false;
      }
      for (const f of advFilters) {
        if (!f.key || !f.op) continue;
        const col = colByKey[f.key]; if (!col) continue;
        const val = col.get(e, ctx);
        if (!applyFilter(val, f.op, f.a, f.b)) return false;
      }
      return true;
    });
    return applyColumnFilters(base, colFilters, colGetValue, Object.fromEntries(
      colsHydrated.map((c) => [
        c.key,
        c.type === "enum" ? "select" : c.type === "date" ? "date" : c.type === "number" ? "number" : "text",
      ])
    ));
  }, [emps, search, advFilters, colByKey, ctx, colFilters, colGetValue, colsHydrated]);

  // sortable on the first visible column
  const sortKey = visibleCols[0] || "code";
  const accessors = Object.fromEntries(colsHydrated.map((c) => [c.key, (r) => c.get(r, ctx)]));
  const { items, sortBy, sortDir, requestSort } = useSortableData(filtered, { initialKey: sortKey, accessors });

  const reset = () => { setSearch(""); setAdvFilters([]); setColFilters({}); };
  const toggleCol = (k) => setVisibleCols((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  const addFilter = () => setAdvFilters((a) => [...a, { key: "code", op: "contains", a: "", b: "" }]);
  const updateFilter = (i, patch) => setAdvFilters((a) => a.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const removeFilter = (i) => setAdvFilters((a) => a.filter((_, idx) => idx !== i));

  const exportCsv = () => {
    const visCols = visibleCols.map((k) => colByKey[k]).filter(Boolean);
    const headerRow = visCols.map((c) => c.label);
    const dataRows = items.map((e) => visCols.map((c) => {
      const v = c.get(e, ctx);
      return v === null || v === undefined ? "" : String(v).replace(/"/g, '""');
    }));
    const csv = [headerRow, ...dataRows].map((row) => row.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `employee_report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportServer = async (format) => {
    const fields = visibleCols
      .map((k) => REPORT_TO_EXPORT_FIELD[k])
      .filter(Boolean);
    const unique = [...new Set(fields)];
    if (unique.length === 0) {
      alert("Select at least one exportable column (Code, Name, Department, etc.).");
      return;
    }
    try {
      await downloadFile(
        `/exports/employees.${format}?fields=${encodeURIComponent(unique.join(","))}`,
        `employees_${new Date().toISOString().slice(0, 10)}.${format}`
      );
    } catch {
      alert("Export failed");
    }
  };

  return (
    <AdminLayout title="Employee Reports" breadcrumbs={["Home", "Reports", "Employees"]}>
      <div className="space-y-4">
        <FilterBar
          filters={[
            companyFilter,
            { type: "search", key: "q", label: "Search", value: search, onChange: setSearch },
          ]}
          onReset={reset}
          rightSlot={
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters((v) => !v)}
                className={`inline-flex items-center gap-1 px-3 py-2 text-sm border rounded ${showFilters || advFilters.length > 0 ? "bg-blue-50 border-blue-300 text-blue-800" : "border-slate-200 hover:bg-slate-50"}`}
                data-testid="report-adv-filter-toggle"
              >
                Advanced Filters {advFilters.length > 0 && <span className="bg-blue-200 text-blue-900 px-1.5 rounded text-[10px]">{advFilters.length}</span>}
              </button>
              <ColumnPicker
                cols={colsHydrated}
                visible={visibleCols}
                onToggle={toggleCol}
                open={showColPicker}
                setOpen={setShowColPicker}
              />
              <button
                onClick={exportCsv}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50"
                data-testid="report-emp-export-csv"
              >
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> CSV
              </button>
              <button
                onClick={() => exportServer("xlsx")}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50"
                data-testid="report-emp-export-xlsx"
              >
                <FileSpreadsheet className="h-4 w-4 text-emerald-700" /> Excel
              </button>
              <button
                onClick={() => exportServer("pdf")}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50"
                data-testid="report-emp-export-pdf"
              >
                <Download className="h-4 w-4 text-rose-600" /> PDF
              </button>
            </div>
          }
        />

        {showFilters && (
          <AdvancedFilters
            colsHydrated={colsHydrated}
            advFilters={advFilters}
            addFilter={addFilter}
            updateFilter={updateFilter}
            removeFilter={removeFilter}
          />
        )}

        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-x-auto" data-testid="employee-report-table">
          <div className="px-5 py-3 text-sm text-slate-500 border-b border-slate-200 flex items-center gap-3 flex-wrap">
            <span>Showing <b>{items.length}</b> of <b>{emps.length}</b> employees · <b>{visibleCols.length}</b> column(s) visible</span>
            {columnFiltersActive(colFilters) && (
              <button type="button" onClick={() => setColFilters({})} className="text-xs text-[#226DB4] hover:underline" data-testid="report-emp-clear-col-filters">
                Clear column filters
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {visibleCols.map((k) => {
                  const c = colByKey[k]; if (!c) return null;
                  return <SortableTH key={k} columnKey={k} active={sortBy} dir={sortDir} onClick={requestSort}>{c.label}</SortableTH>;
                })}
              </tr>
              <ColumnFilterRow
                columns={visibleCols.map((k) => {
                  const c = colByKey[k];
                  if (!c) return { key: k, filterType: "none" };
                  if (c.type === "enum") return { key: k, filterType: "select", options: c.options || [] };
                  if (c.type === "date") return { key: k, filterType: "date" };
                  if (c.type === "number") return { key: k, filterType: "number" };
                  return { key: k, filterType: "text" };
                })}
                filters={colFilters}
                onChange={(key, value) => setColFilters((prev) => ({ ...prev, [key]: value }))}
              />
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? <EmptyState message="No employees match filters" colSpan={visibleCols.length} /> : items.map((e) => (
                <tr key={e.id}>
                  {visibleCols.map((k) => {
                    const c = colByKey[k]; if (!c) return null;
                    const v = c.get(e, ctx);
                    if (c.type === "date") {
                      return <td key={k} className="px-4 py-2 text-slate-700">{formatDisplayDate(v)}</td>;
                    }
                    return <td key={k} className="px-4 py-2 text-slate-700">{v === "" || v === null || v === undefined ? "—" : String(v)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function ColumnPicker({ cols, visible, onToggle, open, setOpen }) {
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50"
        data-testid="report-col-picker-btn"
      >
        Columns ({visible.length})
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-40 w-72 bg-white border border-slate-200 rounded shadow-lg p-2 max-h-80 overflow-y-auto" data-testid="report-col-picker-menu">
            {cols.map((c) => (
              <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={visible.includes(c.key)}
                  onChange={() => onToggle(c.key)}
                  data-testid={`col-toggle-${c.key}`}
                />
                <span>{c.label}</span>
                <span className="ml-auto text-[10px] uppercase text-slate-400">{c.type}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AdvancedFilters({ colsHydrated, advFilters, addFilter, updateFilter, removeFilter }) {
  return (
    <div className="bg-blue-50/40 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="report-adv-filters">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Advanced Filters</h4>
        <button
          onClick={addFilter}
          className="text-xs px-2 py-1 bg-white border border-blue-300 text-blue-800 rounded hover:bg-blue-50"
          data-testid="report-adv-filter-add"
        >
          + Add Filter
        </button>
      </div>
      {advFilters.length === 0 && <div className="text-xs text-slate-500 italic">No filters yet. Click &quot;+ Add Filter&quot; to start.</div>}
      {advFilters.map((f, i) => {
        const col = colsHydrated.find((c) => c.key === f.key);
        const ops = col?.type === "text" ? TEXT_OPS : col?.type === "number" ? NUM_OPS : col?.type === "date" ? DATE_OPS : (col?.type === "enum" ? [{ value: "eq", label: "=" }] : TEXT_OPS);
        return (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`adv-filter-row-${i}`}>
            <select
              value={f.key}
              onChange={(e) => updateFilter(i, { key: e.target.value, op: "contains", a: "", b: "" })}
              className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
              data-testid={`adv-filter-field-${i}`}
            >
              {colsHydrated.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select
              value={f.op}
              onChange={(e) => updateFilter(i, { op: e.target.value })}
              className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
              data-testid={`adv-filter-op-${i}`}
            >
              {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {col?.type === "enum" ? (
              <select value={f.a || ""} onChange={(e) => updateFilter(i, { a: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white" data-testid={`adv-filter-a-${i}`}>
                <option value="">(any)</option>
                {(col.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : col?.type === "date" ? (
              <input type="date" value={f.a || ""} onChange={(e) => updateFilter(i, { a: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white" data-testid={`adv-filter-a-${i}`} />
            ) : (
              <input type={col?.type === "number" ? "number" : "text"} value={f.a || ""} onChange={(e) => updateFilter(i, { a: e.target.value })} placeholder="Value" className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white w-40" data-testid={`adv-filter-a-${i}`} />
            )}
            {f.op === "between" && (
              col?.type === "date"
                ? <input type="date" value={f.b || ""} onChange={(e) => updateFilter(i, { b: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white" data-testid={`adv-filter-b-${i}`} />
                : <input type="number" value={f.b || ""} onChange={(e) => updateFilter(i, { b: e.target.value })} placeholder="…and" className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white w-32" data-testid={`adv-filter-b-${i}`} />
            )}
            <button onClick={() => removeFilter(i)} className="text-rose-500 hover:bg-rose-50 px-2 py-1 rounded text-sm" data-testid={`adv-filter-remove-${i}`}>✕</button>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────── /reports/attendance ─────────── */
const ATT_COL_FILTERS = [
  { key: "code", filterType: "text" },
  { key: "name", filterType: "text" },
  { key: "department_name", filterType: "select", options: [] },
  { key: "Present", filterType: "number" },
  { key: "Absent", filterType: "number" },
  { key: "Leave", filterType: "number" },
  { key: "WFH", filterType: "number" },
  { key: "Weekly Off", filterType: "number" },
  { key: "total", filterType: "number" },
];

const ATT_COL_TYPES = Object.fromEntries(ATT_COL_FILTERS.map((c) => [c.key, c.filterType]));

const SEP_COL_TYPES = {
  code: "text",
  name: "text",
  department: "select",
  manager: "text",
  status: "select",
  exit_type: "select",
  resignation_date: "date",
  last_working_date: "date",
  notice_period_days: "number",
  reason: "text",
};

const PAY_COL_TYPES = {
  employee_code: "text",
  employee_name: "text",
  gross_earnings: "number",
  total_deductions: "number",
  net_pay: "number",
};

export function AttendanceReports() {
  const { companyFilter, empParams, companyId } = useCompanyFilter();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [emps, setEmps] = useState([]);
  const [att, setAtt] = useState([]);
  const [depFilter, setDepFilter] = useState("");
  const [deps, setDeps] = useState([]);
  const [colFilters, setColFilters] = useState({});

  useEffect(() => {
    Promise.all([api.get("/departments"), api.get("/employees", { params: empParams })]).then(([d, e]) => { setDeps(d.data); setEmps(e.data); });
  }, [companyId, empParams]);
  useEffect(() => {
    api.get("/attendance", { params: { month, year, ...empParams } }).then((r) => setAtt(r.data || []));
  }, [month, year, companyId, empParams]);

  const depMap = useMemo(() => Object.fromEntries(deps.map((d) => [d.id, d.name])), [deps]);

  const summary = useMemo(() => {
    const roster = new Set(["Active", "Onboarding", "Probation", "Notice Period"]);
    const filteredEmps = emps.filter((e) => {
      if (!roster.has(e.employment?.status)) return false;
      if (depFilter && e.employment?.department_id !== depFilter) return false;
      return true;
    });
    return filteredEmps.map((e) => {
      const empAtt = att.filter((a) => a.employee_id === e.id);
      const counts = { Present: 0, Absent: 0, Leave: 0, WFH: 0, Holiday: 0, "Weekly Off": 0 };
      empAtt.forEach((a) => { counts[a.status] = (counts[a.status] || 0) + 1; });
      return {
        id: e.id,
        code: e.basic.code,
        name: [e.basic.first_name, e.basic.middle_name, e.basic.last_name].filter(Boolean).join(" "),
        department: e.employment?.department_id,
        department_name: depMap[e.employment?.department_id] || "",
        ...counts,
        total: empAtt.length,
      };
    });
  }, [emps, att, depFilter, depMap]);

  const attColGet = useCallback((r, key) => (key === "department_name" ? r.department_name : r[key]), []);
  const filteredSummary = useMemo(
    () => applyColumnFilters(summary, colFilters, attColGet, ATT_COL_TYPES),
    [summary, colFilters, attColGet]
  );

  const { items, sortBy, sortDir, requestSort } = useSortableData(filteredSummary, { initialKey: "code" });
  const attFilterCols = useMemo(
    () => ATT_COL_FILTERS.map((c) => (c.key === "department_name" ? { ...c, options: deps.map((d) => d.name) } : c)),
    [deps]
  );

  return (
    <AdminLayout title="Attendance Reports" breadcrumbs={["Home", "Reports", "Attendance"]}>
      <div className="space-y-4">
        <FilterBar
          filters={[
            companyFilter,
            { type: "month", key: "month", label: "Month", value: month, onChange: setMonth },
            { type: "year",  key: "year",  label: "Year",  value: year,  onChange: setYear },
            { type: "select", key: "department", label: "Department", value: depFilter, onChange: setDepFilter,
              options: [{ value: "", label: "All" }, ...deps.map((d) => ({ value: d.id, label: d.name }))] },
          ]}
          rightSlot={
            <>
              <button onClick={() => downloadFile(`/exports/attendance.xlsx?month=${month}&year=${year}`, `attendance_${year}_${String(month).padStart(2, "0")}.xlsx`)} className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50" data-testid="report-att-export-xlsx">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
              </button>
              <button onClick={() => downloadFile(`/exports/attendance.pdf?month=${month}&year=${year}`, `attendance_${year}_${String(month).padStart(2, "0")}.pdf`)} className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50" data-testid="report-att-export-pdf">
                <Download className="h-4 w-4 text-rose-600" /> PDF
              </button>
            </>
          }
        />

        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-x-auto">
          {columnFiltersActive(colFilters) && (
            <div className="px-5 py-2 text-xs border-b border-slate-200">
              <button type="button" onClick={() => setColFilters({})} className="text-[#226DB4] hover:underline" data-testid="report-att-clear-col-filters">
                Clear column filters
              </button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <SortableTH columnKey="code" active={sortBy} dir={sortDir} onClick={requestSort}>Code</SortableTH>
                <SortableTH columnKey="name" active={sortBy} dir={sortDir} onClick={requestSort}>Name</SortableTH>
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-600 text-left">Department</th>
                <SortableTH columnKey="Present" active={sortBy} dir={sortDir} onClick={requestSort}>Present</SortableTH>
                <SortableTH columnKey="Absent" active={sortBy} dir={sortDir} onClick={requestSort}>Absent</SortableTH>
                <SortableTH columnKey="Leave" active={sortBy} dir={sortDir} onClick={requestSort}>Leave</SortableTH>
                <SortableTH columnKey="WFH" active={sortBy} dir={sortDir} onClick={requestSort}>WFH</SortableTH>
                <SortableTH columnKey="Weekly Off" active={sortBy} dir={sortDir} onClick={requestSort}>Weekly Off</SortableTH>
                <SortableTH columnKey="total" active={sortBy} dir={sortDir} onClick={requestSort}>Total</SortableTH>
              </tr>
              <ColumnFilterRow
                columns={attFilterCols}
                filters={colFilters}
                onChange={(key, value) => setColFilters((prev) => ({ ...prev, [key]: value }))}
              />
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? <EmptyState message="No data for the selected period" colSpan={9} /> : items.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2">{r.code}</td>
                  <td className="px-4 py-2">{r.name}</td>
                  <td className="px-4 py-2 text-slate-600">{r.department_name || "—"}</td>
                  <td className="px-4 py-2">{r.Present}</td>
                  <td className="px-4 py-2">{r.Absent}</td>
                  <td className="px-4 py-2">{r.Leave}</td>
                  <td className="px-4 py-2">{r.WFH}</td>
                  <td className="px-4 py-2">{r["Weekly Off"]}</td>
                  <td className="px-4 py-2 font-semibold">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

/* ─────────── /reports/separations ─────────── */
const deriveExitType = (e) => {
  const reason = (e.employment?.exit_reason || "").toLowerCase();
  const status = e.employment?.status || "";

  if (status === "Terminated" || reason.includes("termin")) return "Terminated";
  if (status === "Abscond" || reason.includes("abscond")) return "Abscond";
  if (reason.includes("contract ended")) return "Contract End";

  return "Resignation"; /* above section updated by SM */
};

export function SeparationReports() {
  const { companyFilter, empParams, companyId } = useCompanyFilter();
  const [emps, setEmps] = useState([]);
  const [deps, setDeps] = useState([]);
  const [status, setStatus] = useState(""); // Notice Period | Resigned | "" (both)
  const [exitType, setExitType] = useState("");
  const [depFilter, setDepFilter] = useState("");
  const [managerFilter, setManagerFilter] = useState("");
  const [resignFrom, setResignFrom] = useState("");
  const [resignTo, setResignTo] = useState("");
  const [lwdFrom, setLwdFrom] = useState("");
  const [lwdTo, setLwdTo] = useState("");
  const [search, setSearch] = useState("");
  const [colFilters, setColFilters] = useState({});

  useEffect(() => {
    Promise.all([api.get("/employees", { params: empParams }), api.get("/departments")]).then(([e, d]) => {
      setEmps(e.data);
      setDeps(d.data);
    });
  }, [companyId, empParams]);

  const depMap = useMemo(() => Object.fromEntries(deps.map((d) => [d.id, d.name])), [deps]);
  const empMap = useMemo(
    () => Object.fromEntries(emps.map((e) => [e.id, [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ")])),
    [emps]
  );

  const separated = useMemo(() => emps.filter((e) => !!e.employment?.exit_initiated_at), [emps]);

  const filtered = useMemo(() => {
    const inRange = (val, from, to) => {
      if (!val) return false;
      const d = String(val).slice(0, 10);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    };
    return separated.filter((e) => {
      const emp = e.employment || {};
      if (status && emp.status !== status) return false;
      if (exitType && deriveExitType(e) !== exitType) return false;
      if (depFilter && emp.department_id !== depFilter) return false;
      if (managerFilter && emp.manager_id !== managerFilter) return false;
      if ((resignFrom || resignTo) && !inRange(emp.resignation_date, resignFrom, resignTo)) return false;
      if ((lwdFrom || lwdTo) && !inRange(emp.last_working_date, lwdFrom, lwdTo)) return false;
      if (search) {
        const s = search.toLowerCase();
        const full = [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ").toLowerCase();
        if (!full.includes(s) && !(e.basic?.code || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [separated, status, exitType, depFilter, managerFilter, resignFrom, resignTo, lwdFrom, lwdTo, search]);

  const rows = useMemo(() => filtered.map((e) => ({
    id: e.id,
    code: e.basic?.code,
    name: [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" "),
    department: depMap[e.employment?.department_id] || "",
    manager: empMap[e.employment?.manager_id] || "",
    status: e.employment?.status,
    exit_type: deriveExitType(e),
    resignation_date: e.employment?.resignation_date || "",
    last_working_date: e.employment?.last_working_date || "",
    notice_period_days: e.employment?.notice_period_days === "" || e.employment?.notice_period_days == null
      ? ""
      : Number(e.employment.notice_period_days),
    reason: e.employment?.exit_reason || "",
  })), [filtered, depMap, empMap]);

  const sepColGet = useCallback((r, key) => r[key], []);
  const filteredRows = useMemo(
    () => applyColumnFilters(rows, colFilters, sepColGet, SEP_COL_TYPES),
    [rows, colFilters, sepColGet]
  );

  const { items, sortBy, sortDir, requestSort } = useSortableData(filteredRows, { initialKey: "resignation_date" });

  const reset = () => {
    setStatus(""); setExitType(""); setDepFilter(""); setManagerFilter("");
    setResignFrom(""); setResignTo(""); setLwdFrom(""); setLwdTo(""); setSearch("");
    setColFilters({});
  };

  const exportCsv = () => {
    const header = ["Code", "Name", "Department", "Manager", "Status", "Exit Type", "Resignation Date", "LWD", "Notice (days)", "Reason"];
    const csv = [header, ...items.map((r) => [
      r.code, r.name, r.department, r.manager, r.status, r.exit_type,
      r.resignation_date, r.last_working_date, r.notice_period_days, (r.reason || "").replace(/"/g, '""'),
    ])]
      .map((row) => row.map((c) => `"${c ?? ""}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `employee_separations_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="Employee Separation Report" breadcrumbs={["Home", "Reports", "Separations"]}>
      <div className="space-y-4">
        <FilterBar
          filters={[
            companyFilter,
            { type: "search", key: "q", label: "Search", value: search, onChange: setSearch },
            { type: "select", key: "status", label: "Status", value: status, onChange: setStatus, options: [{ value: "", label: "All Separation Statuses" }, { value: "Notice Period", label: "Notice Period" }, { value: "Resigned", label: "Resigned" }, { value: "Terminated", label: "Terminated" }, { value: "Abscond", label: "Abscond" }] },
            { type: "select", key: "exit_type", label: "Exit Type", value: exitType, onChange: setExitType, options: [{ value: "", label: "All" }, { value: "Resignation", label: "Resignation" }, { value: "Terminated", label: "Terminated" }, { value: "Abscond", label: "Abscond" }, { value: "Contract End", label: "Contract End" }] },
            { type: "select", key: "department", label: "Department", value: depFilter, onChange: setDepFilter, options: [{ value: "", label: "All" }, ...deps.map(d => ({ value: d.id, label: d.name }))] },
            { type: "select", key: "manager", label: "Manager", value: managerFilter, onChange: setManagerFilter, options: [{ value: "", label: "All" }, ...emps.map(e => ({ value: e.id, label: [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ") }))] },
            { type: "date", key: "resign_from", label: "Resigned From", value: resignFrom, onChange: setResignFrom },
            { type: "date", key: "resign_to", label: "Resigned To", value: resignTo, onChange: setResignTo },
            { type: "date", key: "lwd_from", label: "LWD From", value: lwdFrom, onChange: setLwdFrom },
            { type: "date", key: "lwd_to", label: "LWD To", value: lwdTo, onChange: setLwdTo }
          ]}
          onReset={reset}
          rightSlot={
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50"
              data-testid="report-sep-export-csv"
            >
              <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> CSV
            </button>
          }
        />

        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-x-auto" data-testid="separation-report-table">
          <div className="px-5 py-3 text-sm text-slate-500 border-b border-slate-200 flex items-center gap-3 flex-wrap">
            <span>Showing <b>{items.length}</b> of <b>{separated.length}</b> separations</span>
            {columnFiltersActive(colFilters) && (
              <button type="button" onClick={() => setColFilters({})} className="text-xs text-[#226DB4] hover:underline" data-testid="report-sep-clear-col-filters">
                Clear column filters
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <SortableTH columnKey="code" active={sortBy} dir={sortDir} onClick={requestSort}>Code</SortableTH>
                <SortableTH columnKey="name" active={sortBy} dir={sortDir} onClick={requestSort}>Name</SortableTH>
                <SortableTH columnKey="department" active={sortBy} dir={sortDir} onClick={requestSort}>Department</SortableTH>
                <SortableTH columnKey="manager" active={sortBy} dir={sortDir} onClick={requestSort}>Manager</SortableTH>
                <SortableTH columnKey="status" active={sortBy} dir={sortDir} onClick={requestSort}>Status</SortableTH>
                <SortableTH columnKey="exit_type" active={sortBy} dir={sortDir} onClick={requestSort}>Exit Type</SortableTH>
                <SortableTH columnKey="resignation_date" active={sortBy} dir={sortDir} onClick={requestSort}>Resigned</SortableTH>
                <SortableTH columnKey="last_working_date" active={sortBy} dir={sortDir} onClick={requestSort}>LWD</SortableTH>
                <SortableTH columnKey="notice_period_days" active={sortBy} dir={sortDir} onClick={requestSort}>Notice</SortableTH>
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-600 text-left">Reason</th>
              </tr>
              <ColumnFilterRow
                columns={[
                  { key: "code", filterType: "text" },
                  { key: "name", filterType: "text" },
                  { key: "department", filterType: "select", options: deps.map((d) => d.name) },
                  { key: "manager", filterType: "text" },
                  { key: "status", filterType: "select", options: ["Notice Period", "Resigned", "Terminated", "Abscond"] },
                  { key: "exit_type", filterType: "select", options: ["Resignation", "Terminated", "Abscond", "Contract End"] },
                  { key: "resignation_date", filterType: "date" },
                  { key: "last_working_date", filterType: "date" },
                  { key: "notice_period_days", filterType: "number" },
                  { key: "reason", filterType: "text" },
                ]}
                filters={colFilters}
                onChange={(key, value) => setColFilters((prev) => ({ ...prev, [key]: value }))}
              />
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? <EmptyState message="No separations match filters" colSpan={10} /> : items.map((r) => (
                <tr key={r.id} data-testid={`sep-row-${r.code}`}>
                  <td className="px-4 py-2 text-slate-700">{r.code}</td>
                  <td className="px-4 py-2 text-slate-800">{r.name}</td>
                  <td className="px-4 py-2 text-slate-600">{r.department || "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{r.manager || "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${r.status === "Resigned" ? "bg-rose-100 text-rose-700" : "bg-orange-100 text-orange-700"}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{r.exit_type}</td>
                  <td className="px-4 py-2 text-slate-600">{formatDisplayDate(r.resignation_date)}</td>
                  <td className="px-4 py-2 text-slate-600">{formatDisplayDate(r.last_working_date)}</td>
                  <td className="px-4 py-2 text-slate-600">{r.notice_period_days === "" ? "—" : `${r.notice_period_days}d`}</td>
                  <td className="px-4 py-2 text-slate-500 max-w-md truncate" title={r.reason}>{r.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
export function PayrollReports() {
  const { companyFilter, empParams, companyId } = useCompanyFilter();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [payslips, setPayslips] = useState([]);
  const [colFilters, setColFilters] = useState({});

  useEffect(() => {
    api.get("/payslips", { params: { month, year, ...empParams } }).then((r) => setPayslips(r.data || []));
  }, [month, year, companyId, empParams]);

  const payRows = useMemo(() => payslips.map((p) => ({
    ...p,
    gross_earnings: Number(p.gross_earnings || 0),
    total_deductions: Number(p.total_deductions || 0),
    net_pay: Number(p.net_pay || 0),
  })), [payslips]);

  const payColGet = useCallback((r, key) => r[key], []);
  const filteredPayslips = useMemo(
    () => applyColumnFilters(payRows, colFilters, payColGet, PAY_COL_TYPES),
    [payRows, colFilters, payColGet]
  );

  const { items, sortBy, sortDir, requestSort } = useSortableData(filteredPayslips, { initialKey: "employee_code" });

  const totals = useMemo(() => {
    return items.reduce((acc, p) => {
      acc.gross += Number(p.gross_earnings || 0);
      acc.ded += Number(p.total_deductions || 0);
      acc.net += Number(p.net_pay || 0);
      return acc;
    }, { gross: 0, ded: 0, net: 0 });
  }, [items]);

  return (
    <AdminLayout title="Payroll Reports" breadcrumbs={["Home", "Reports", "Payroll"]}>
      <div className="space-y-4">
        <FilterBar
          filters={[
            companyFilter,
            { type: "month", key: "month", label: "Month", value: month, onChange: setMonth },
            { type: "year",  key: "year",  label: "Year",  value: year,  onChange: setYear },
          ]}
          rightSlot={
            <>
              <button onClick={() => downloadFile(`/exports/salary-register.xlsx?month=${month}&year=${year}`, `salary_register_${year}_${String(month).padStart(2, "0")}.xlsx`)} className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50" data-testid="report-pay-export-xlsx">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
              </button>
              <button onClick={() => downloadFile(`/exports/salary-register.pdf?month=${month}&year=${year}`, `salary_register_${year}_${String(month).padStart(2, "0")}.pdf`)} className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50" data-testid="report-pay-export-pdf">
                <Download className="h-4 w-4 text-rose-600" /> PDF
              </button>
              <button
                onClick={() =>
                  downloadFile(
                    `/exports/bank-payment.xlsx?month=${month}&year=${year}`,
                    `bank_payment_${year}_${String(month).padStart(2, "0")}.xlsx`
                  )
                }
                className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50"
                data-testid="report-pay-export-bank"
                title="Bank payment upload file"
              >
                <FileSpreadsheet className="h-4 w-4 text-[#226DB4]" /> Bank Payment
              </button>
            </>
          }
        />

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded p-4"><div className="text-xs text-slate-500">Total Gross</div><div className="text-2xl font-semibold text-slate-800">₹{totals.gross.toLocaleString("en-IN")}</div></div>
          <div className="bg-white border border-slate-200 rounded p-4"><div className="text-xs text-slate-500">Total Deductions</div><div className="text-2xl font-semibold text-rose-600">₹{totals.ded.toLocaleString("en-IN")}</div></div>
          <div className="bg-white border border-slate-200 rounded p-4"><div className="text-xs text-slate-500">Total Net</div><div className="text-2xl font-semibold text-emerald-600">₹{totals.net.toLocaleString("en-IN")}</div></div>
        </div>

        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-x-auto">
          {columnFiltersActive(colFilters) && (
            <div className="px-5 py-2 text-xs border-b border-slate-200">
              <button type="button" onClick={() => setColFilters({})} className="text-[#226DB4] hover:underline" data-testid="report-pay-clear-col-filters">
                Clear column filters
              </button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <SortableTH columnKey="employee_code" active={sortBy} dir={sortDir} onClick={requestSort}>Code</SortableTH>
                <SortableTH columnKey="employee_name" active={sortBy} dir={sortDir} onClick={requestSort}>Name</SortableTH>
                <SortableTH columnKey="gross_earnings" active={sortBy} dir={sortDir} onClick={requestSort}>Gross</SortableTH>
                <SortableTH columnKey="total_deductions" active={sortBy} dir={sortDir} onClick={requestSort}>Deductions</SortableTH>
                <SortableTH columnKey="net_pay" active={sortBy} dir={sortDir} onClick={requestSort}>Net Pay</SortableTH>
              </tr>
              <ColumnFilterRow
                columns={[
                  { key: "employee_code", filterType: "text" },
                  { key: "employee_name", filterType: "text" },
                  { key: "gross_earnings", filterType: "number" },
                  { key: "total_deductions", filterType: "number" },
                  { key: "net_pay", filterType: "number" },
                ]}
                filters={colFilters}
                onChange={(key, value) => setColFilters((prev) => ({ ...prev, [key]: value }))}
              />
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? <EmptyState message="No payslips for the selected period" colSpan={5} /> : items.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-2">{p.employee_code}</td>
                  <td className="px-4 py-2">{p.employee_name}</td>
                  <td className="px-4 py-2">₹{Number(p.gross_earnings || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2 text-rose-600">₹{Number(p.total_deductions || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2 text-emerald-600 font-semibold">₹{Number(p.net_pay || 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
